import { Directive, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSelect } from "@angular/material/select";
import { AuthorizedUser, ConfigStorage, getFormErrorList, isObjectValidator, requiredIfValidator, UserStorage } from "@scisys-uim/uim-common";
import { AssetsUser, Firearm, FIREARM_ASSIGNABLE_PERSON_METADATA, FIREARM_ASSIGNABLE_PERSON_TYPE, FIREARM_STORAGE_LOCATION_METADATA, FIREARM_STORAGE_LOCATION_TYPE, FirearmAssignablePerson, FirearmAssignment, FirearmStorageLocation, Ranger, RangerFirearmAssignmentLog } from "assets-model";
import { FirearmAssignablePersonService, FirearmStorageLocationService, RangerFirearmAssignmentLogService, RangerService } from "assets-service";
import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from "rxjs";
import { filter, map, tap } from "rxjs/operators";
import { AutocompleteStringFilterFunctionAsync } from "../../../shared/form/autocomplete.util";



export interface RangerReturnFirearmsToStorageDialogData {
  storageAssignee?: FirearmAssignablePerson;
  location?: FirearmStorageLocation;
  comments?: string;
  ranger?: Ranger;
}

export interface RangerReturnFirearmsToStorageDialogConfigData {
  ranger?: Ranger;
  storageAssignee?: FirearmAssignablePerson;
  storageAssigneeSelectionListFilterLoad?: (text: string) => Observable<FirearmAssignablePerson[]>;
}

@Directive()
export abstract class RangerReturnFirearmsToStorageDialogComponent implements OnInit {

  protected systemConfig: any;

  public form: FormGroup;

  public item: RangerReturnFirearmsToStorageDialogData = {};

  protected userDetails: AuthorizedUser;
  rangerFirearmAssignmentLog: RangerFirearmAssignmentLog;

  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  get currentAssignments(): FirearmAssignment[] {
    const assignments = this.rangerFirearmAssignmentLog?.assignments.filter(a => a.currentAssignment) || [];
    return assignments;
  }

  get currentFirearms(): Firearm[] {
    const firearms = this.currentAssignments?.map(a => a.firearm);
    return firearms;
  }

  get currentPoolFirearmCount(): number {
    const firearms = this.currentFirearms?.filter(f => f.poolFirearm);
    return firearms?.length || 0;
  }

  get currentNonPoolFirearmCount(): number {
    const firearms = this.currentFirearms?.filter(f => !f.poolFirearm);
    return firearms?.length || 0;
  }

  get hasOnlyPoolFirearms(): boolean {
    return this.currentFirearmCount == this.currentPoolFirearmCount;
  }

  get hasPoolAndNonPoolFirearms(): boolean {
    return this.currentPoolFirearmCount > 0 && this.currentNonPoolFirearmCount > 0;
  }

  get hasOnlyNonPoolFirearms(): boolean {
    return this.currentFirearmCount == this.currentNonPoolFirearmCount;
  }

  get currentFirearmCount(): number {
    return this.rangerFirearmAssignmentLog?.currentFirearmCount || 0;
  }

  get hasPendingActions(): boolean {
    return this.rangerFirearmAssignmentLog?.rangerActionPendingCount > 0 || this.rangerFirearmAssignmentLog?.firearmActionPendingCount > 0
  }

  private _onInitCompleteSubject: Subject<boolean> = new BehaviorSubject<boolean>(null);
  $onInitComplete: Observable<boolean> = this._onInitCompleteSubject.asObservable().pipe(filter(o => o != null));
  private onInitSubscription: Subscription = null;

  @ViewChild('storageAssignee') storageAssigneeInput: MatSelect;
  storageAssigneeSelectionList: FirearmAssignablePerson[] = [];

  @ViewChild('location') locationInput: MatSelect;
  locationSelectionList: FirearmStorageLocation[] = [];

  locationSelectionListFilterLoad: AutocompleteStringFilterFunctionAsync = (text?: string) => {
    if (this.item?.storageAssignee?.businessUnit == null) {
      return of(null);
    }
    return this.firearmStorageLocationService.findByFullFiltersExtended({
      includeNational: true,
      buId: this.item?.storageAssignee?.businessUnit?.buId,
      text: text
    }, null, true, true).pipe(map(response => response.items.map(item => item.location)));
  }

  constructor(
    protected dialogRef: MatDialogRef<RangerReturnFirearmsToStorageDialogComponent, RangerReturnFirearmsToStorageDialogData>,
    public data: RangerReturnFirearmsToStorageDialogConfigData,
    protected fb: FormBuilder,
    protected dialog: MatDialog,
    protected rangerService: RangerService,
    protected rangerFirearmAssignmentLogService: RangerFirearmAssignmentLogService,
    protected firearmAssignablePersonService: FirearmAssignablePersonService,
    protected firearmStorageLocationService: FirearmStorageLocationService,
    protected configService: ConfigStorage,
    protected userStorage: UserStorage) {

    dialogRef.disableClose = true;

    this.data = this.data || {
    };

    this.form = this.fb.group({
      storageAssignee: [null, [isObjectValidator,]],
      location: [null],
      comments: [null]
    }, {
      validators: [
        requiredIfValidator(() => this.storageAssignee, () => this.currentFirearmCount > 0, "Storage Assignee", { required: "Storage Assignee is required for storage of current firearms." }),
        requiredIfValidator(() => this.location, () => this.currentFirearmCount > 0, "Location", { required: "Location is required for storage of current firearms." }),
        requiredIfValidator(() => this.comments, () => this.currentFirearmCount > 0, "Comment", { required: "Storage Assignment Comment is required for storage of current firearms." }),
      ]
    });

    this.rangerFirearmAssignmentLogService.get(data.ranger.rangerId).subscribe(result => {
      this.rangerFirearmAssignmentLog = result;
      this.setDefaultDisabledFields();
    });

    this.item.storageAssignee = this.data.storageAssignee;

    this.setDefaultDisabledFields();
  }

  protected onInitAsyncComplete() {
    this.storageAssigneeLoadSelectionList();
    this.locationLoadSelectionList();
  }

  ngAfterViewInit() {
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    this.storageAssignee?.enable();
    if (this.data.storageAssignee != null) {
      this.storageAssignee?.disable();
    }
    if (this.hasOnlyPoolFirearms) {
      this.storageAssignee?.disable();
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  getFormErrorList() {
    var result = [...getFormErrorList(this.form)];
    return result;
  }

  // Person field


  storageAssigneeLoadSelectionList() {
    this.firearmAssignablePersonService.findByFullFilters({
      buId: this.data.ranger?.businessUnit?.buId,
      assignmentType: 'STORAGE'
    }).subscribe(
      data => {
        this.storageAssigneeSelectionList = FIREARM_ASSIGNABLE_PERSON_TYPE.entityDecorator.ensureContained(this.item && this.item.storageAssignee, data.items);
        if (this.item.storageAssignee == null && this.storageAssigneeSelectionList?.length == 1) {
          this.item.storageAssignee = this.storageAssigneeSelectionList[0];
        }
      }
    );
  }

  storageAssigneeDisplay(item: FirearmAssignablePerson) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_ASSIGNABLE_PERSON_TYPE.description(item);
  }

  storageAssigneeChanging(value: FirearmAssignablePerson, unsetDependents: boolean = true) { }

  storageAssigneeChanged(value: FirearmAssignablePerson, unsetDependents: boolean = true) {
    this.locationLoadSelectionList();
  }

  storageAssigneeCompare(a: any, b: any) {
    return FIREARM_ASSIGNABLE_PERSON_METADATA.equals(a, b);
  }

  get storageAssignee() {
    return this.control('storageAssignee');
  }

  // Location field

  locationLoadSelectionList() {
    this.firearmStorageLocationService.findByFullFilters({
      includeNational: true,
      buId: this.data.ranger?.businessUnit?.buId,
    }).subscribe(
      data => {
        this.locationSelectionList = FIREARM_STORAGE_LOCATION_TYPE.entityDecorator.ensureContained(this.item && this.item.location, data.items);
      }
    );
  }

  locationDisplay(item: FirearmStorageLocation) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return FIREARM_STORAGE_LOCATION_TYPE.description(item);
  }

  locationChanging(value: string, unsetDependents: boolean = true) { }

  locationChanged(value: string, unsetDependents: boolean = true) {
  }

  locationCompare(a: any, b: any) {
    return FIREARM_STORAGE_LOCATION_METADATA.equals(a, b);
  }

  get location() {
    return this.control('location');
  }

  // Comments field
  commentsChanging(value: string, unsetDependents: boolean = true) { }

  commentsChanged(value: string, unsetDependents: boolean = true) {
  }

  get comments() {
    return this.control('comments');
  }

  // form controls general

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  // 'framework'

  ngOnInit() {
    this.onInitAsync();
  }


  protected onInitAsync() {
    const allObservables: Observable<any>[] = this.onInitAsyncObservables() || [];
    this.onInitSubscription = combineLatest(allObservables).subscribe(() => {
      this.onInitSubscription?.unsubscribe();
      this._onInitAsyncComplete();
    });
  }

  protected _onInitAsyncComplete() {
    this.onInitAsyncComplete();
    this.configService.itemChanges().subscribe((configData: any) => this.initConfig(configData));
    this._onInitCompleteSubject.next(true);
    this._onInitCompleteSubject.complete();
  }

  // These observables will be waited for, on page load, before the screen is ready to search.
  // These Observables are required by the framework - take care in overriding
  protected onInitAsyncObservables(): Observable<any>[] {
    return [this.onInitConfigObservable(),
    this.onInitUserDetails()];
  }
  protected onInitConfigObservable(): Observable<any> {
    return this.configService.resolve().pipe(tap((configData: any) => this.initConfig(configData)));
  }
  protected initConfig(config: any) {
    this.systemConfig = config || {};
  }
  protected onInitUserDetails(): Observable<AuthorizedUser> {
    return this.userStorage.nextNonNullItem().pipe(tap((userDetails: AuthorizedUser) => this.initUserDetails(userDetails)));
  }
  protected initUserDetails(userDetails: AuthorizedUser) {
    this.userDetails = userDetails;
  }


  disabledTooltip(element) {
    return element && (element.valid || !element.touched || !element.value);
  }
}